//!#system	

import apiService from '@/apiService';
import Vue from 'vue';
import series from 'to-series';
import store from '@/store';

// initial state
const state = {
    loaded: false,
    user: {},
    isAuthenticated: false,
    isAdmin: false,
    preferences: {}, // user pref object
    loadedPrefs: false,
    userPrefs_id: null,
    userList: [] // cached users
};

const userFetchingLog = [];

// getters
const getters = {
    preferences: () => {
        return state.preferences;
    },
    /**
     * Get a user preference for the current user. (getter)
     * @param  {string} key - The key for the preference in the preferences object you want.
     * @return {object}        - The preference value (any type you have set)
     * @example
     * let pref = this.$store.getters['authentication/pref'](prefkey);
     */
    pref: (state) => (key) => {
        return state.preferences[key] || {};
    },
    isLoaded: () => {
        return state.loaded;
    },
    pins: (state) => !!state.preferences.pinsNavOpen,
    openPins: (state) => {
        if (Array.isArray(state.preferences.openPins)) {
            return state.preferences.openPins;
        }
        return [];
    },
    userPrefsAreLoaded: (state) => {
        return state.loadedPrefs;
    },
    currentUser: (state) => {
        return state.user;
    }
};

/**
 * Get the user preferences for the current logged in user.  Internal to this module.
 * @param  {Function} done - Callback
 */
const getUserPrefs = function(done) {
    if (!state.user.id) return done(new Error('No user object loaded.'));
    const routeInfo = {
        namespace: 'uac',
        pluralName: 'userPrefs'
    };
    let userPrefs_id = null;
    let prefs = {};
    series()
        .first(cb => {
            if (state.userPrefs_id) return cb();
            apiService.list({
                ...routeInfo,
                filters: {
                    userKey: state.user.id
                }
            }, (err, response) => {
                if (err) return cb(err);
                if (response && response.length > 0) userPrefs_id = response[0].id;
                return cb();
            });
        })
        .next(cb => {
            if (!userPrefs_id) return cb();
            apiService.detail({ ...routeInfo, id: state.userPrefs_id || userPrefs_id }, (err, response) => {
                if (err) return cb(err);
                prefs = response.prefs || {};
                return cb();
            });
        })
        .end(err => {
            return done(err, prefs, userPrefs_id);
        });
};

/**
 * Set the preferences for this user.  Merges in the object you pass to the object that is the preferences.  Internal to this module.
 * @param {Object}   prefsToMerge - The object to merge into the preferences.
 * @param {Function} done         - Callback
 */
const setUserPrefs = function(prefsToMerge, done) {
    if (!state.user.id) return done(new Error('No user object loaded.'));
    let prefs = {};
    let userPrefs_id = null;
    let exists = false;
    const routeInfo = {
        namespace: 'uac',
        pluralName: 'userPrefs',
        id: state.user.id
    };
    series()
        .first(cb => {
            if (!state.userPrefs_id) return cb();
            apiService.detail({ ...routeInfo, id: state.userPrefs_id }, (err, response) => {
                if (err && err.errcode === 'C003') {
                    exists = false;
                    return cb();
                }
                exists = true;
                if (err) return cb(err);
                prefs = { ...prefs, ...response.prefs };
                return cb();
            });
        })
        .next(cb => {
            if (exists) return cb();
            apiService.create({ ...routeInfo, body: { userKey: state.user.id } }, (err, response) => {
                if (err) return cb(err);
                prefs = { ...prefs, ...response.prefs };
                userPrefs_id = response.id;
                return cb();
            });
        })
        .next(cb => {
            prefs = { ...prefs, ...prefsToMerge };
            apiService.update({ ...routeInfo, id: state.userPrefs_id || userPrefs_id, body: { prefs } }, (err, response) => {
                if (err) return cb(err);
                prefs = { ...prefs, ...response.prefs };
                return cb();
            });
        })
        .end(err => {
            return done(err, prefs, userPrefs_id);
        });
};

const userLookup = function(id, refresh, callback) {
    const lookup = state.userList.filter(u => u.id === id);
    if (lookup && lookup.length > 0 && refresh === false) return callback(null, lookup[0]);
    if (userFetchingLog.includes(id)) {
        // another call is trying to get the user.
        // try again in a bit
        return setTimeout(() => {
            userLookup(id, refresh, callback);
        }, 250);
    }
    userFetchingLog.push(id);
    apiService.call({
        method: 'GET',
        path: '/_sys/internals/uac/user-get-minor/' + encodeURIComponent(id),
    }, (err, response) => {
        if (err) return callback(err);
        let userFetchingFind = userFetchingLog.findIndex(e => e === id);
        if (userFetchingFind >= 0) userFetchingLog.splice(userFetchingFind, 1);
        return callback(null, response);
    })
};

// actions
const actions = {
    setLoaded({ commit }) {
        commit('setStateLoaded');
    },
    /**
     * Get the preferences for the current user.  This is run on first-load of the UI and does not need to be called again. To get a preference in a component, use the getter.
     * @param  {Function} options.commit - State function
     */
    getPrefs({ commit }, { callback }) {
        getUserPrefs((err, prefs, userPrefs_id) => {
            if (!err) {
                commit('setUserPrefs', prefs);
                commit('setPrefsLoaded');
                if (userPrefs_id) commit('setUserPrefId', userPrefs_id);
            }
            if (callback) return callback(err);
        });
    },
    /**
     * Set preferences for the current user. Your preferences object will be merged into the user preferences object.  Try to not have your preferences be too deep, since this does a shallow merge.
     * @param {Function} options.commit       - State function
     * @param {object} options.prefsToMerge - Your object to merge
     * @param {Function} [options.callback] - Optional callback
     * @example
     *
     * this.$store.dispatch('authentication/setPrefs', { prefsToMerge, callback: () => {
     *   // do something
     * }});
     */
    setPrefs({ commit }, { prefsToMerge, callback }) {
        setUserPrefs(prefsToMerge, (err, prefs, userPrefs_id) => {
            if (!err) {
                commit('setUserPrefs', prefs);
                if (userPrefs_id) commit('setUserPrefId', userPrefs_id);
                if (callback) {
                    callback();
                }
            }
        });
    },
    changePinSetting({ commit }, { pinSetting, callback }) {
        setUserPrefs({ pinsNavOpen: pinSetting }, (err, prefs, userPrefs_id) => {
            if (!err) {
                commit('setUserPrefs', prefs);
                if (userPrefs_id) commit('setUserPrefId', userPrefs_id);
                if (callback) {
                    callback();
                }
            }
        });
    },
    pin({ commit }, { path, icon, name, tooltip , callback }) {
        commit('setPin', { path, icon, name, tooltip });
        setUserPrefs({ openPins: state.preferences.openPins }, (err, prefs, userPrefs_id) => {
            if (!err) {
                commit('setUserPrefs', prefs);
                if (userPrefs_id) commit('setUserPrefId', userPrefs_id);
                if (callback) {
                    callback();
                }
            }
        });
    },
    unpin({ commit }, { path, icon, name, tooltip , callback }) {
        commit('unsetPin', { path, icon, name, tooltip });
        setUserPrefs({ openPins: state.preferences.openPins }, (err, prefs, userPrefs_id) => {
            if (!err) {
                commit('setUserPrefs', prefs);
                if (userPrefs_id) commit('setUserPrefId', userPrefs_id);
                if (callback) {
                    callback();
                }
            }
        });
    },
    userGet({ commit }, { id, callback, refresh }) {
        userLookup(id, refresh, (err, response) => {
            if (err) return callback(err);
            if (response) commit('cacheUser', response);
            return callback(null, response);
        });
    },
    userRegister({ commit }, { email, username, password, name, userType, callback }) {
        apiService.call({
            method: 'POST',
            path: '/_sys/internals/users/register',
            params: {
                username: username,
                password: password,
                name: name,
                emailAddress: email,
                userType: userType
            }
        }, (err, response, meta) => {
            if (!err && response.id) {
                commit('setUser', response);
                commit('setIsAuthenticated', false);
            }
            return callback(err, response, meta);
        });
    },
    userSignin({ commit }, { email, password, callback }) {
        apiService.call({
            method: 'POST',
            path: '/uac/users/action/authenticate/' + encodeURIComponent(email),
            params: {
                password: password,
                emailAddress: email
            }
        }, (err, response, meta) => {
            if (!err && response.id && response.signins && response.signins.length) {
                commit('setUser', response);
                commit('setIsAuthenticated', true);
                store.dispatch('app/clearObjectError', {});
            }
            return callback(err, response, meta);
        });
    },
    userLoad({ commit }, { callback }) {
        apiService.call({
            method: 'POST',
            path: '/uac/users/action/authload'
        }, (err, response, meta) => {
            if (!err) {
                commit('setUser', response);
                commit('setIsAuthenticated', true);
                callback(err, response, meta);
            } else {
                callback(err);
            }
        });
    },
    userSignout({ commit }, { callback }) {
        apiService.call({
            method: 'POST',
            path: '/uac/users/action/signout/' + encodeURIComponent(state.user.emailAddress)
        }, (err, response, meta) => {
            commit('setUser', {});
            commit('setIsAuthenticated', false);
            callback(err, response, meta);
        });
    },
    userInvalidate({ commit }) {
        commit('setUser', {});
        commit('setIsAuthenticated', false);
    },
    userIsAdmin({ commit }, { callback }) {
        apiService.isAdmin((err, response) => {
            if (response && 'isAdmin' in response) {
                commit('setIsAdmin', response.isAdmin);
            } else {
                commit('setIsAdmin', false);
            }
        });
        if (callback) {
            callback();
        }
    }
};

// mutations
const mutations = {
    setStateLoaded() {
        state.loaded = true;
    },
    setUser(state, payload) {
        state.user = payload;
    },
    setIsAuthenticated(state, payload) {
        state.isAuthenticated = payload;
    },
    setIsAdmin(state, payload) {
        state.isAdmin = payload;
    },
    setUserPrefId(state, payload) {
        state.userPrefs_id = payload;
    },
    setUserPrefs(state, payload) {
        state.preferences = payload;
    },
    setPrefsLoaded(state, payload) {
        state.loadedPrefs = true;
    },
    setPin(state, payload) {
        if (!Array.isArray(state.preferences.openPins)) state.preferences.openPins = [];
        const finder = state.preferences.openPins.find(p => p.path === payload.path);
        if (!finder) state.preferences.openPins.push(payload);
    },
    unsetPin(state, payload) {
        if (!Array.isArray(state.preferences.openPins)) state.preferences.openPins = [];
        const finder = state.preferences.openPins.find(p => p.path === payload.path);
        if (finder) {
            let indexToRemove = -1;
            indexToRemove = state.preferences.openPins.findIndex(el => el.path === payload.path);
            if (indexToRemove >= 0) state.preferences.openPins.splice(indexToRemove, 1);
        }
    },
    cacheUser(state, payload) {
        const lookup = state.userList.filter(u => u.id === payload.id);
        if (!lookup || lookup.length === 0) {
            state.userList.push(payload);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};